import React, { useState, useRef, useEffect, useContext } from "react";
import { format } from "date-fns";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import useSound from "use-sound";
import { Badge, IconButton, List, ListItem, ListItemText, Popover } from "@material-ui/core";
import NotificationsActiveOutlined from "@material-ui/icons/NotificationsActiveOutlined";
import NotificationsOutlined from "@material-ui/icons/NotificationsOutlined";

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import alertSound from "../../assets/audios/sound.mp3";
import SocketNotifications from "../../context/Socket/Listeners/SocketNotifications";
import TicketListItem from "../TicketListItem";
import useTickets from "../../hooks/useTickets";

import { useGlobalRegisters } from "../../context/GlobalRegisters/GlobalRegistersContext";

const useStyles = makeStyles(theme => ({
  tabContainer: {
    overflowY: "auto",
    maxHeight: 350,
    ...theme.scrollbarStyles,
  },

  popoverPaper: {
    width: "100%",
    maxWidth: 350,
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(-10),
    [theme.breakpoints.down("sm")]: {
      maxWidth: 270,
    },
  },

  popoverPaperAppBar: {
    width: 270,
    marginLeft: theme.spacing(0),
    marginRight: theme.spacing(0),
    marginTop: theme.spacing(0),
  },

  notificationIconDrawerOpen: {
    borderRadius:"10px",
    margin: theme.spacing(1, 0, 0, -0.5),
    padding: "5px 30px 5px 10px",
  },

  notificationIconDrawerClose: {
    borderRadius:"10px",
    margin: theme.spacing(1, 0, 0, 1),
    padding: "5px 30px 5px 10px",
  },
}));

const NotificationsPopOver = ({ drawerOpen, isAtAppBar }) => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const ticketIdUrl = +history.location.pathname.split("/")[2];
  const ticketIdRef = useRef(ticketIdUrl);

  const anchorEl = useRef();
  const soundAlertRef = useRef();

  const { tickets } = useTickets({ withUnreadMessages: "true" });
  const [play] = useSound(alertSound);

  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [, setDesktopNotifications] = useState([]);

  const [iconButtonClassNames, setIconButtonClassNames] = useState("");

  const { getSetting, allUsers, isLoading } = useGlobalRegisters();
  

  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    setIconButtonClassNames(drawerOpen ? `${classes.notificationIconDrawerOpen}` : `${classes.notificationIconDrawerClose}`);
  }, [drawerOpen, classes]);

  useEffect(() => {
    const pendingTickets = tickets.filter(ticket => ticket.status === "pending");
    const sumUnreadMessages = pendingTickets.reduce((total, current) => current.unreadMessages > 0 ? total + 1 : total, 0);
    document.title = sumUnreadMessages > 0 ? `(${sumUnreadMessages}) BestZap` : "BestZap";
  }, [tickets]);

  useEffect(() => {
    soundAlertRef.current = play;
    if (!("Notification" in window)) toast.info(i18n.t("notifications.notificationsNotSupported"));
    else Notification.requestPermission();
  }, [play]);

  useEffect(() => {
    if (isLoading) return;

    setNotifications(prevNotifications => {
      const updatedNotifications = tickets
        .filter(ticket => ticket.status !== "closed")
        .map(ticket => {
          const index = prevNotifications.findIndex(notification => {
            return notification.ticket.id === ticket.id && [user.id, -1].includes(notification.notifiedUser);
          });

          if (index !== -1) return { ...prevNotifications[index], ticket };
          if (ticket.status === "group") return allUsers.map(user => ({ notifiedUser: user.id, ticket }));
          return [{ notifiedUser: -1, ticket }];
        });

      return [].concat(...updatedNotifications).filter(Boolean);
    });

  }, [isLoading, allUsers, tickets, user]);

  useEffect(() => {
    ticketIdRef.current = ticketIdUrl;
  }, [ticketIdUrl]);



  //  ********************
  //  ** Sub Components **
  //  ********************
  const NotificationTicket = ({ children }) => {
    return (
      <div onClick={handleClickAway}>
        {children}
      </div>
    );
  };



  //  ***************
  //  ** Functions **
  //  ***************
  const handleNotifications = async (data, sendNotificationsChatsGroupsValue, sendNotificationsValue) => {
    if (!("Notification" in window)) {
      toast.info(i18n.t("notifications.notificationsNotSupported"));
      return;
    }
    
    const { message, contact, ticket } = data;

    Notification.requestPermission(function(result) {
      const chatNotificationCondition = (sendNotificationsValue === "both" && ["open", "pending"].includes(data.ticket.status))
        || (sendNotificationsValue === "open" && data.ticket.status === "open")
        || (sendNotificationsValue === "pending" && data.ticket.status === "pending");

      const notificationSettingCondition =
        (sendNotificationsChatsGroupsValue === "both" && (data.ticket.status === "group" || chatNotificationCondition))
        || (sendNotificationsChatsGroupsValue === "groups" && data.ticket.status === "group")
        || (sendNotificationsChatsGroupsValue === "chats" && chatNotificationCondition);

      if (result !== "granted" || !notificationSettingCondition) return;

      navigator.serviceWorker.ready.then(async function(registration) {
        const notification = registration.showNotification(
          `${i18n.t("tickets.notification.message")} ${contact?.name || i18n.t("tickets.notification.internalChat")}`
          , {
            body: `${message.body} - ${format(new Date(), "HH:mm")}`
            , icon: contact?.profilePicUrl
            , tag: ticket.id
            , renotify: true
            , vibrate: [200, 100, 200]
          }
        );

        setDesktopNotifications(notifications => {
          const notificationIndex = notifications.findIndex(notification => notification.tag === ticket.id);

          if (notificationIndex !== -1) {
            notifications[notificationIndex] = notification;
            return [...notifications];
          }

          return [notification, ...notifications];
        });

        //  ************************
        //  ** Notification Sound **
        //  ************************
        const shouldUseNotificationSound = [3, 4].includes(ticket.type)
          ? getSetting("notificationSoundForInternalChats") === "enabled"
          : true;

        const isGeneralFlagNotificationSoundEnabled = localStorage.getItem("flagNotificationSound") === "true";
        if (isGeneralFlagNotificationSoundEnabled && shouldUseNotificationSound) soundAlertRef.current();
      });
    });
  };

  const handleClick = () => {
    setIsOpen(prevState => !prevState);
  };

  const handleClickAway = () => {
    setIsOpen(false);
  };

  const renderNotificationsList = () => {
    const filteredNotifications = notifications.filter(notification => [user.id, -1].includes(notification.notifiedUser));

    if (filteredNotifications.length === 0) {
      return (
        <ListItem>
          <ListItemText>{i18n.t("notifications.noTickets")}</ListItemText>
        </ListItem>
      );
    }

    return (
      filteredNotifications.map(notification => (
        <NotificationTicket key={notification?.ticket?.id}>
          <TicketListItem ticket={notification?.ticket} />
        </NotificationTicket>
      ))
    );
  };

  

  //  ************
  //  ** Return **
  //  ************
  return (
    <>
      <SocketNotifications
        setNotifications={setNotifications}
        setDesktopNotifications={setDesktopNotifications}
        ticketIdRef={ticketIdRef}
        handleNotifications={handleNotifications}
      />

      <IconButton
        onClick={handleClick}
        ref={anchorEl}
        aria-label="Open Notifications"
        color="inherit"
        className={iconButtonClassNames}
      >
        <Badge
          badgeContent={notifications.filter(notification => [user.id, -1].includes(notification.notifiedUser)).length}
          color="primary"
        >
          {notifications
            .filter(notification => [user.id, -1].includes(notification.notifiedUser))
            .length === 0 ? <NotificationsOutlined /> : <NotificationsActiveOutlined />}
        </Badge>
      </IconButton>

      <Popover
        disableScrollLock
        open={isOpen}
        anchorEl={anchorEl.current}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        classes={{ paper: isAtAppBar ? classes.popoverPaperAppBar : classes.popoverPaper}}
        onClose={handleClickAway}
      >
        <List dense className={classes.tabContainer}>
          {renderNotificationsList()}
        </List>
      </Popover>
    </>
  );
};

export default NotificationsPopOver;